import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, User, Mail, Phone, Wrench, CreditCard as Edit2, Save, X, AlertCircle } from 'lucide-react';

interface Personnel {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  specialties: string[] | null;
  active: boolean;
  created_at: string;
}

const SPECIALTY_OPTIONS = [
  'Plumbing',
  'Electrical',
  'HVAC',
  'Appliance',
  'Structural',
  'Landscaping',
  'Pest Control',
  'General Maintenance',
  'Carpentry',
  'Painting',
];

export function PersonnelManager() {
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialties: [] as string[],
    active: true,
  });

  useEffect(() => {
    loadPersonnel();
  }, []);

  const loadPersonnel = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('maintenance_personnel')
        .select('*')
        .order('name');

      if (error) throw error;
      setPersonnel(data || []);
    } catch (err) {
      console.error('Error loading personnel:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      specialties: [],
      active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (person: Personnel) => {
    setFormData({
      name: person.name,
      email: person.email || '',
      phone: person.phone || '',
      specialties: person.specialties || [],
      active: person.active,
    });
    setEditingId(person.id);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const personnelData = {
        name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        specialties: formData.specialties.length > 0 ? formData.specialties : null,
        active: formData.active,
      };

      if (editingId) {
        const { error } = await supabase
          .from('maintenance_personnel')
          .update(personnelData)
          .eq('id', editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('maintenance_personnel')
          .insert(personnelData);

        if (error) throw error;
      }

      resetForm();
      loadPersonnel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save personnel');
    }
  };

  const toggleSpecialty = (specialty: string) => {
    setFormData((prev) => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter((s) => s !== specialty)
        : [...prev.specialties, specialty],
    }));
  };

  const toggleActive = async (id: string, currentActive: boolean) => {
    try {
      const { error } = await supabase
        .from('maintenance_personnel')
        .update({ active: !currentActive })
        .eq('id', id);

      if (error) throw error;
      loadPersonnel();
    } catch (err) {
      alert('Failed to update status: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading personnel...</div>;
  }

  const activePersonnel = personnel.filter((p) => p.active);
  const inactivePersonnel = personnel.filter((p) => !p.active);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Maintenance Personnel</h2>
          <p className="text-sm text-gray-600 mt-1">
            {activePersonnel.length} active {activePersonnel.length === 1 ? 'worker' : 'workers'}
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-700 transition-colors"
        >
          <Plus size={20} />
          Add Personnel
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-5 bg-secondary-50 border border-secondary-200 rounded-lg space-y-4">
          <h3 className="font-semibold text-gray-800 text-lg">
            {editingId ? 'Edit Personnel' : 'Add New Personnel'}
          </h3>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.active ? 'active' : 'inactive'}
                onChange={(e) => setFormData({ ...formData, active: e.target.value === 'active' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Specialties</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALTY_OPTIONS.map((specialty) => (
                <button
                  key={specialty}
                  type="button"
                  onClick={() => toggleSpecialty(specialty)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    formData.specialties.includes(specialty)
                      ? 'bg-secondary text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {specialty}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-secondary-700 transition-colors"
            >
              <Save size={18} />
              {editingId ? 'Update Personnel' : 'Add Personnel'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {activePersonnel.length === 0 && inactivePersonnel.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <User className="mx-auto mb-3 text-gray-400" size={48} />
          <p>No maintenance personnel added yet</p>
          <p className="text-sm mt-1">Add personnel to assign them to tickets</p>
        </div>
      ) : (
        <div className="space-y-6">
          {activePersonnel.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Active Personnel</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activePersonnel.map((person) => (
                  <PersonnelCard
                    key={person.id}
                    person={person}
                    onEdit={handleEdit}
                    onToggleActive={toggleActive}
                  />
                ))}
              </div>
            </div>
          )}

          {inactivePersonnel.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-600 mb-3">Inactive Personnel</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {inactivePersonnel.map((person) => (
                  <PersonnelCard
                    key={person.id}
                    person={person}
                    onEdit={handleEdit}
                    onToggleActive={toggleActive}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface PersonnelCardProps {
  person: Personnel;
  onEdit: (person: Personnel) => void;
  onToggleActive: (id: string, currentActive: boolean) => void;
}

function PersonnelCard({ person, onEdit, onToggleActive }: PersonnelCardProps) {
  return (
    <div
      className={`border rounded-lg p-4 transition-all ${
        person.active
          ? 'border-gray-200 hover:border-secondary-300 hover:shadow-md'
          : 'border-gray-200 bg-gray-50 opacity-75'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className={`p-2 rounded-lg ${person.active ? 'bg-secondary-100' : 'bg-gray-200'}`}>
            <User className={person.active ? 'text-secondary-600' : 'text-gray-500'} size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-800 truncate">{person.name}</h3>
            <span
              className={`inline-block px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                person.active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {person.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        <button
          onClick={() => onEdit(person)}
          className="text-gray-500 hover:text-secondary-600 transition-colors"
          title="Edit"
        >
          <Edit2 size={18} />
        </button>
      </div>

      {(person.email || person.phone) && (
        <div className="space-y-1.5 mb-3">
          {person.email && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Mail size={14} className="flex-shrink-0" />
              <span className="truncate">{person.email}</span>
            </div>
          )}
          {person.phone && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Phone size={14} className="flex-shrink-0" />
              <span>{person.phone}</span>
            </div>
          )}
        </div>
      )}

      {person.specialties && person.specialties.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5">
            <Wrench size={12} />
            <span className="font-medium">Specialties:</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {person.specialties.map((specialty) => (
              <span
                key={specialty}
                className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
              >
                {specialty}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => onToggleActive(person.id, person.active)}
        className={`w-full px-3 py-1.5 rounded text-sm font-medium transition-colors ${
          person.active
            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            : 'bg-green-100 text-green-700 hover:bg-green-200'
        }`}
      >
        {person.active ? 'Deactivate' : 'Activate'}
      </button>
    </div>
  );
}
